// src/com/trans_plan_travel/TransPlanTravel.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { IconButton, CircularProgress } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import HomeplusPentagon from '../../asset/image/homeplus_pentagon.svg';
import HomeplusNoLine from '../../asset/image/homeplus_no_line.svg';
import SearchBox from '../../common/SearchBox';
import {ContentConainer, ContentHeader, CountLabel, ControlBox, ToolBox} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { useAlertSetter } from '../../api/app-hook';
import ValueMisc from '../../value/ValueMisc';
import VzmSelector from '../../common/VzmSelector';
import { dcInfoListRepo, transPlanTravleListRepo, userInfoRepo } from '../../value/AppModel';
import { LIST_TRANS_PLAN_TRAVEL, reformTransPlanTravelList, transPlanTravelInclude } from './TransPlanTravelGql';
import { LIST_DC_INFO } from '../dc_info/DcInfoGql';
import RadioSelector from '../../common/RadioSelector';

const HeaderHeight = 40;
const RowHeight = 72;
const PoiHeight = 32;
const SubInfoHeight = 20;
const ToGoRoadColor = '#f00';
const DoneRoadColor = '#555';

const TravelContainer = styled(Box)({
    flexGrow: 1, width: '100%',
    position:'relative',
    whiteSpace:'nowrap',
    border: '1px solid #ddd',
    borderRadius: 3,
});

const TableContainer = styled(Box)({
    position:'absolute', top:0, left:0, bottom:0, ////////backgroundColor: '#8f8',
    overflowX: 'auto', width:'100%',
    overflowY: 'auto', // 내부에서 TableBodyBox가 상하 스크롤.
});
const TableBodyBox = styled(Box)({
    position: "relative", ////////backgroundColor:'yellow',
    paddingTop: HeaderHeight,
});
const TableHeaderRow = styled(Box)({
    position: 'absolute',
    display:'flex', fontSize: '0.9rem',
    top:0, left:0, right:0, height: HeaderHeight,
    overflow:'hidden',
    background:'linear-gradient(#dddddd, #eaeaea, #ededed, #f0f0f0, #f2f2f2, #f0f0f0, #ededed, #eaeaea, #dddddd)'
});

const RowBox = styled(Box)({
    display: 'flex', fontSize: '0.85rem',
    '&:nth-of-type(even)': {backgroundColor: '#f4f4f4'},
    minHeight: RowHeight,
});
const CellBox = styled(Box)({
    borderRight: '1px solid #ddd', padding: 5,
    display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
});
const IndexCell = styled(CellBox)({width: 55});
const CompanyCell = styled(CellBox)({width: 180});
const VehicleCell = styled(CellBox)({width: 160});
const GraphCell = styled(CellBox)({
    flexGrow: 1, justifyContent: 'start',
    display: 'flex', alignItems: 'center',
});

const TruckLabel = styled(Box)({
    textAlign: 'center', padding: 5, fontWeight:'bold', //borderBottom: AppStyle.BorderCCC
});
const DriverLabel = styled(Box)({textAlign: 'center', fontSize:'0.8rem'});
const MobileLabel = styled(Box)({textAlign: 'center', fontSize:'0.8rem', color:'#555'})

const DcContainer = styled(Box)({
    display: 'flex', alignItems: 'center', padding: 5, border: AppStyle.BorderCCC, borderRadius: 5,
    backgroundColor: '#d9e0f7', width: 120, marginLeft:5, height: PoiHeight, boxSizing: 'border-box',
});
const DcLabel = styled(Box)({
    flexGrow: 1,
    display: 'flex', alignItems: 'center', justifyContent:'center', marginLeft: 5,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
});

const DcBox = ({dcName}) => {
    return (
        <DcContainer>
            <img src={HomeplusPentagon} alt={dcName} height={18} style={{marginLeft:5}}/>
            <DcLabel>{dcName}</DcLabel>
        </DcContainer>
    )
};

const NodeBox = styled(Box)({
    display: 'flex', alignItems: 'center',
});
// In NodeBox: <RoadMark/>, <PoiBox/>
const RoadMark = styled(Box)({height: 5, width: 26});
const PoiBox = styled(Box)({width: 154});
// In PoiBox: <HochaLabelBox/> <PoiLabel/>, <TimeLabel/>
const HochaLabelBox = styled('div')({textAlign: 'left'});
const HochaLabel = styled('span')({
    fontSize: '0.7rem', height: SubInfoHeight, width: SubInfoHeight,
    backgroundColor: '#d9e0f7', border: '1px solid #b9c0d7', borderRadius: SubInfoHeight/2,
    boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
});
const PoiLabel = styled(Box)({
    height: PoiHeight-4, display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    backgroundColor: AppStyle.PrimaryRGB, color: 'white', borderRadius: 5,
    fontSize: '0.84rem'
});
const TimeLabel = styled('div')({
    display: 'flex', alignItems: 'center', justifyContent: 'end',
    fontSize: '0.7rem', color: '#337', paddingTop: 2, height: SubInfoHeight,
});
const EtdAndEtaBox = styled(Box)({
    position: 'fixed', zIndex: 1000, padding: 8,
    border: '1px solid #ccc', borderRadius: 8, backgroundColor: '#d9e0f7',
    fontSize: '0.8rem'
});


const EffectStage = {start:0, checkedDcList:1, checkedParam:2};
const BindFilter = {all:'all', bind:'bind', nonBind:'sole'};
const BindFilterOptions = [
    {value:BindFilter.all, label:'전체'},
    {value:BindFilter.bind, label:'동적'},
    {value:BindFilter.nonBind, label:'일반'},
];
/**
 * 배송상태 현황.
 * DC에서 출발, 각 점포에의 도착 정보.
 */
export default function TransPlanTravel() {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const transPlanTravleListData = useReactiveVar(transPlanTravleListRepo);
    const [travels, setTravels] = useState([]); // from transPlanTravleListRepo, filtered by searchText
    const [gotResult, setGotResult] = useState(false); // 데이터 없음 표시를 위해.
    const dcInfoListData = useReactiveVar(dcInfoListRepo); // for selection.
    const [bindFilter, setBindFilter] = useState(BindFilter.all);
    const [searchText, setSearchText] = useState('');
    const [etdAndEta, setEtdAndEta] = useState(null);
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const runRef = useRef(EffectStage.start);
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getDcInfoList, responseDcList] = useLazyQuery(LIST_DC_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetDcList(data, option)},
        onError: (error) => addAlert({title: '출발지 목록 조회 오류', message: [error.message]})
    });

    const [getTransPlanTravelList, responseTravelList] = useLazyQuery(LIST_TRANS_PLAN_TRAVEL, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data) => onCompleteGetTransPlanTravelList(data),
        onError: (error) => addAlert({title: "배송현황 조회 오류", message: [error.message]})
    });

    const getTransPlanTravelListNow = (planDate, dcCd) => {
        getTransPlanTravelList({variables:{planDate, dcCd}});
    };

    useEffect(()=>{
        if(runRef.current === EffectStage.start) {
            if(!sessionInfo.isTransCompanyUser() && !sessionInfo.isStoreUser()) {
                const diff = new Date().getTime()/1000 - dcInfoListData.when;
                if(diff > 300) getDcInfoList();
            }
            runRef.current += 1; // becomes checkedDcList
            setGotResult(false);
        }
    }, [dcInfoListData, sessionInfo]);

    useEffect(()=>{
        if(runRef.current === EffectStage.checkedDcList) {
            let dcCd = null;
            const newData = {...transPlanTravleListData};
            let changed = false;
            if(!sessionInfo.isTransCompanyUser() && !sessionInfo.isStoreUser()) {
                if(newData.dcCd) dcCd = newData.dcCd;
                else {
                    dcCd = sessionInfo.user.dscIdAs;
                    newData.dcCd = dcCd;
                    changed = true;
                }
            }
            let planDate;//  = ValueMisc.getYMD();
            if(newData.planDate) planDate = newData.planDate;
            else {
                planDate = ValueMisc.getYMD();
                newData.planDate = planDate;
                changed = true;
            }
            if(changed) transPlanTravleListRepo(newData);
            getTransPlanTravelListNow(planDate, dcCd);

            runRef.current += 1; // becomes checkedParam
        }
    }, [transPlanTravleListData, sessionInfo]);

    // filter travels by searchText
    useEffect(()=>{
        setTravels(
            transPlanTravleListData.records
            .filter((item)=>{
                if(bindFilter === BindFilter.bind) return item.nodes.length > 1;
                if(bindFilter === BindFilter.nonBind) return item.nodes.length === 1;
                return true;
            })
            .filter((item)=>transPlanTravelInclude(item, searchText.trim()))
        );
    }, [transPlanTravleListData, bindFilter, searchText]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetDcList = (data, clientOption) => {
        if(data.dcInfoList) {
            dcInfoListRepo({
                when:new Date().getTime()/1000,
                records: data.dcInfoList
            });
        }
    };

    const onCompleteGetTransPlanTravelList = (data) => {
        if(data.transPlanTravelList) {
            const newData = {...transPlanTravleListData};
            newData.records = reformTransPlanTravelList(data.transPlanTravelList);
            transPlanTravleListRepo(newData);
            lastReloadRef.current = new Date().getTime()/1000;
            setGotResult(true);
        }
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<
    const onChangePlanDate = (date)=>{
        // setDatePicked(date);
        const ymd = ValueMisc.getYMD(date.toDate());
        const newData = {...transPlanTravleListData};
        newData.planDate = ymd;
        getTransPlanTravelListNow(ymd, newData.dcCd);
    };

    const onChangeDcCd = (id, value) => {
        const newData = {...transPlanTravleListData};
        newData.dcCd = value;
        transPlanTravleListRepo(newData);
        getTransPlanTravelListNow(newData.planDate, value);
    };

    const onClickReload = () =>{
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else getTransPlanTravelListNow(transPlanTravleListData.planDate, transPlanTravleListData.dcCd);
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onMouseEnterPoi = (event, node) =>{
        const xFixedPos = event.clientX + 10;
        const yFixedPos = event.clientY + 10;
        const etd = '출발예상 ' + (node.prosDptrTime ? node.prosDptrTime.substring(0, 16) : '데이터 없음');
        const eta = '도착예상 ' + (node.prosArrTime ? node.prosArrTime.substring(0, 16) : '데이터 없음');
        setEtdAndEta({x:xFixedPos, y:yFixedPos, etd, eta, storeName: node.storeName});
    };

    const onMouseLeavePoi = (event) =>{
        setEtdAndEta(null);
    };

    const loadingAny = responseDcList.loading || responseTravelList.loading;

    const renderNodeBox = (node, group) => {
        const roadColor = node.timeArrival && group.vehPlates ? DoneRoadColor : ToGoRoadColor;
        return(
            <NodeBox key={node.storeId}>
                <RoadMark style={{backgroundColor: roadColor}}/>
                <PoiBox>
                    <HochaLabelBox>
                        <HochaLabel>{node.hocha}</HochaLabel>
                    </HochaLabelBox>
                    <PoiLabel
                        onMouseEnter={(event)=>onMouseEnterPoi(event, node)}
                        onMouseLeave={(event)=>onMouseLeavePoi(event, node)}
                    >
                        {node.storeName}
                    </PoiLabel>
                    <TimeLabel>
                        {
                            node.timeArrival
                            ?
                            '도착: '+node.timeArrival.substring(11, 16)
                            : ' '
                        }
                    </TimeLabel>
                </PoiBox>
            </NodeBox>
        );
    };

    const renderRow = (item, index) => {
        return(
            <RowBox key={item.planId}>
                <IndexCell>{index+1}</IndexCell>
                <CompanyCell>{item.comName}</CompanyCell>
                <VehicleCell>
                    {
                        item.vehPlates
                        ?
                        <Box>
                            <TruckLabel>{item.vehPlates}</TruckLabel>
                            <DriverLabel>
                                {item.driverName}
                            </DriverLabel>
                            <MobileLabel>
                                {
                                    item.driverMobile ?
                                    item.driverMobile : '-'
                                }
                            </MobileLabel>
                        </Box>
                        :
                        '미지정'
                    }
                </VehicleCell>
                <GraphCell>
                    <DcBox dcName={item.dcName} />
                    {
                        item.nodes.map((node)=>renderNodeBox(node, item))
                    }
                </GraphCell>
            </RowBox>
        );
    };

    const renderNoRecord = () => {
        return(
            <Box
                style={{
                    display:'flex', flexDirection:'column',
                    justifyContent:'center', alignItems:'center',
                    height: '40vh',
                    fontSize: '3rem', color: '#9df'
                }}
            >
                <Box>
                    {
                        gotResult ?
                        '데이터가 없습니다.' :
                        '잠시만 기다려 주세요'
                    }</Box>
                <img
                    src={HomeplusNoLine}
                    alt='Homeplus'
                    height={30}
                    style={{opacity:0.5, marginTop:20}}
                />
            </Box>
        )
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <ContentConainer>
                <ContentHeader>
                    <ControlBox>
                        <DatePicker label={'출발요청일'}
                            sx={{width:150}}
                            slotProps={{
                                openPickerIcon:{fontSize:AppWord.SMALL},
                                textField:{size:AppWord.SMALL}
                            }}
                            format='YYYY-MM-DD'
                            value={transPlanTravleListData.planDate ? dayjs(transPlanTravleListData.planDate) : ''}
                            onChange={onChangePlanDate}
                            disabled={loadingAny}
                        />
                        {
                            !sessionInfo.isTransCompanyUser()
                            && !sessionInfo.isStoreUser()
                            && dcInfoListData.records.length > 0
                            &&
                            <VzmSelector
                                dict={{
                                    id: 'dcCd',
                                    valueKey: 'dcCd',
                                    labelKey: 'dcName',
                                    label: '출발지',
                                }}
                                value={transPlanTravleListData.dcCd || sessionInfo.user.dscIdAs}
                                onChange={onChangeDcCd}
                                list={dcInfoListData.records}
                                style={{marginLeft:10}}
                            />
                        }
                        &nbsp; &nbsp;
                        <RadioSelector
                            dict={{
                                id: 'value',
                                selectList: BindFilterOptions
                            }}
                            value={bindFilter}
                            onChange={(id, value) => setBindFilter(value)}
                            horizontal={true}
                        />
                        <SearchBox
                            value={searchText}
                            onChangeText={onChangeSearchText}
                            width={200}
                            style={{marginLeft:10}}
                        />
                    </ControlBox>
                    <ToolBox>
                        <CountLabel count={travels.length} />
                        {
                            loadingAny ?
                            <CircularProgress size={20} />
                            :
                            <IconButton
                                color={AppStyle.mui.inherit}
                                onClick={onClickReload}
                            >
                                <CachedIcon />
                            </IconButton>
                        }
                    </ToolBox>
                </ContentHeader>
                <TravelContainer>
                    <TableContainer>
                        <TableBodyBox>
                            {
                                travels.length === 0
                                ?
                                renderNoRecord()
                                :
                                travels.map((item, index)=>renderRow(item, index))
                            }
                        </TableBodyBox>
                    </TableContainer>
                    <TableHeaderRow>
                        <IndexCell>No.</IndexCell>
                        <CompanyCell>운송사</CompanyCell>
                        <VehicleCell>차량, 운전자</VehicleCell>
                        <GraphCell>운행상황</GraphCell>
                    </TableHeaderRow>
                </TravelContainer>
            </ContentConainer>
            {
                etdAndEta &&
                <EtdAndEtaBox style={{left:etdAndEta.x, top:etdAndEta.y - 60}}>
                    <Box>{etdAndEta.storeName}</Box>
                    <Box>{etdAndEta.etd}</Box>
                    <Box>{etdAndEta.eta}</Box>
                </EtdAndEtaBox>
            }
        </LocalizationProvider>
    );
}